import { useState } from "react";
import { toast } from "sonner";
import { Mail, Phone, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";

export const Contact = ({ property }) => {
  const { t } = useLang();
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "", sms_opt_in: false, sms_marketing_opt_in: false });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/inquiries", form);
      toast.success(t("Message sent! We'll reply within 24 hours."));
      setForm({ name: "", email: "", phone: "", message: "", sms_opt_in: false, sms_marketing_opt_in: false });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="py-20 md:py-28 bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-14 items-start">
        <div>
          <p className="text-primary font-medium text-sm uppercase tracking-widest mb-2">{t("Get in touch")}</p>
          <h2 className="font-script text-6xl md:text-8xl leading-tight text-foreground mb-5">
            {t("Have a question?")}
          </h2>
          <p className="text-muted-foreground text-lg leading-relaxed mb-8">
            {t("We're happy to help you plan the perfect family stay. Reach out and we'll get right back to you.")}
          </p>

          <div className="space-y-4">
            <a href={`mailto:${property?.contact_email}`} className="flex items-center gap-3 text-foreground hover:text-primary transition-colors">
              <span className="h-11 w-11 rounded-full bg-accent flex items-center justify-center"><Mail className="h-5 w-5 text-secondary" strokeWidth={1.5} /></span>
              {property?.contact_email}
            </a>
            {property?.whatsapp_number && (
              <div className="flex items-center gap-3 text-foreground">
                <span className="h-11 w-11 rounded-full bg-accent flex items-center justify-center"><Phone className="h-5 w-5 text-secondary" strokeWidth={1.5} /></span>
                {property?.whatsapp_number}
              </div>
            )}
          </div>
        </div>

        <form onSubmit={submit} data-testid="inquiry-form" className="bg-card rounded-2xl p-7 md:p-9 shadow-sm space-y-5">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="c-name">{t("Name")}</Label>
              <Input id="c-name" data-testid="inquiry-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5 rounded-lg" placeholder={t("Your name")} />
            </div>
            <div>
              <Label htmlFor="c-phone">{t("Mobile phone (for text updates)")}</Label>
              <Input id="c-phone" type="tel" inputMode="tel" data-testid="inquiry-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1.5 rounded-lg" placeholder="+1 ..." />
            </div>
          </div>
          <div>
            <Label htmlFor="c-email">{t("Email")}</Label>
            <Input id="c-email" type="email" data-testid="inquiry-email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5 rounded-lg" placeholder="you@email.com" />
          </div>
          <div>
            <Label htmlFor="c-msg">{t("Message")}</Label>
            <Textarea id="c-msg" data-testid="inquiry-message" required value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="mt-1.5 rounded-lg min-h-[120px]" placeholder={t("Tell us about your trip...")} />
          </div>
          <label className="flex items-start gap-2.5 cursor-pointer" data-testid="inquiry-sms-consent-label">
            <Checkbox
              checked={form.sms_opt_in}
              onCheckedChange={(v) => setForm({ ...form, sms_opt_in: !!v })}
              className="mt-0.5"
              data-testid="inquiry-sms-opt-in"
            />
            <span className="text-xs text-muted-foreground leading-relaxed">
              {t("I agree to receive reservation-related text messages from MYAZBNB.COM at the mobile number above (replies to my inquiry, booking details). Message frequency varies. Msg & data rates may apply. Reply HELP for help, STOP to cancel.")}{" "}
              <Link to="/sms" className="text-primary underline">{t("SMS Terms")}</Link> · <Link to="/privacy" className="text-primary underline">{t("Privacy")}</Link>
            </span>
          </label>
          <label className="flex items-start gap-2.5 cursor-pointer" data-testid="inquiry-sms-marketing-consent-label">
            <Checkbox
              checked={form.sms_marketing_opt_in}
              onCheckedChange={(v) => setForm({ ...form, sms_marketing_opt_in: !!v })}
              className="mt-0.5"
              data-testid="inquiry-sms-marketing-opt-in"
            />
            <span className="text-xs text-muted-foreground leading-relaxed">
              {t("(Optional) I also agree to receive promotional text messages about special offers and discounts from MYAZBNB.COM. Consent is not a condition of any purchase. Msg & data rates may apply. Reply STOP to cancel.")}
            </span>
          </label>
          <Button type="submit" disabled={loading} data-testid="inquiry-submit" className="w-full rounded-full bg-primary hover:bg-primary/90 h-12 hover:-translate-y-0.5 transition-all">
            {loading ? t("Sending...") : <><Send className="h-4 w-4 mr-2" /> {t("Send Message")}</>}
          </Button>
        </form>
      </div>
    </section>
  );
};
